import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiEndpoints } from "../../../helper/ApiEndpoints";
import ApiRequest from "../../../helper/ApiRequest";
import CustomSelect from "../../../component/CustomSelect";
import CustomImageUpload from "../../../component/CustomImageUpload";
import CustomPdfUpload from "../../../component/CustomPdfUpload";
import DynamicCKEditor from "../../../component/DynamicCkEditor";
import { toast } from "react-toastify";
import AdminHeader from "../../../component/AdminHeader";
import { BaseUrl } from "../../../helper/BaseUrl";

const editEndpoint = ApiEndpoints.product.edit;
const updateEndpoint = ApiEndpoints.product.update;

export default function ProductEdit() {
  const navigate = useNavigate();
  const { product_id } = useParams();

  const [productCategory, setproductCategory] = useState([]);

  const [previews, setPreviews] = useState({ thumbnail: null, pdf_brochure: null, images: [null] });
  const [postData, setPostData] = useState({ title: "", slug: "", brand: "", type: "", capacity: "", no_of_rolls_and_roll_dia: "", model_number: "", category_id: "", brief: "", description: "", thumbnail: "", alt_thumbnail: "", pdf_brochure: "", status: "0", images: [{ image: null, alt_image: "" }, { image: null, alt_image: "" }], specifications: [] });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ApiRequest("GET", editEndpoint + product_id);
        setPostData(response?.product || {});
        setproductCategory(response?.product_categories || []);

        setPreviews((prev) => ({
          ...prev,
          thumbnail: response?.product ? BaseUrl + response?.product?.thumbnail : prev?.thumbnail,
          pdf_brochure: BaseUrl + response.product.pdf_brochure,
          images: response.product.images?.map(img =>
            img.image ? BaseUrl + img.image : null
          ) || []
        }))
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        setproductCategory([]);
      }
    };

    fetchData();
  }, [product_id]);

  const handleImageChange = (index, file) => {
    const updatedImages = [...postData.images];
    updatedImages[index].image = file;

    setPostData(prev => ({
      ...prev,
      images: updatedImages
    }));

    const updatedPreviews = [...previews.images];
    updatedPreviews[index] = URL.createObjectURL(file);

    setPreviews(prev => ({
      ...prev,
      images: updatedPreviews
    }));
  };

  const handleAltChange = (index, value) => {
    const updatedImages = [...postData.images];
    updatedImages[index].alt_image = value;

    setPostData(prev => ({
      ...prev,
      images: updatedImages
    }));
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      const file = files[0];
      const isPdf = file.type === "application/pdf";

      setPreviews((prev) => ({
        ...prev,
        [name]: URL.createObjectURL(file),
        ...(isPdf && { pdf_brochure: file.name }),
      }));

      setPreviews((prev) => ({
        ...prev,
        [name]: URL.createObjectURL(file),
      }));

      setPostData((prev) => ({
        ...prev,
        [name]: file,
      }));
    } else {
      // For non-file inputs, update the postData state directly
      setPostData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleEditorChange = (key, data) => {
    setPostData((prevState) => ({
      ...prevState,
      [key]: data,
    }));
  };

  const isEmptyValue = (value) => {
    if (value === null || value === "") return true;

    // allow File objects
    if (value instanceof File) return false;

    // arrays
    if (Array.isArray(value)) {
      return value.some(item => isEmptyValue(item));
    }

    // objects
    if (typeof value === "object") {
      return Object.values(value).some(v => isEmptyValue(v));
    }

    return false;
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    for (const [key, value] of Object.entries(postData)) {
      if (key === "type" || key === "capacity" || key === "no_of_rolls_and_roll_dia" || key === "model_number" || key === "banner_image" ||  key === "alt_banner_image") continue;

      if (isEmptyValue(value)) {
        toast.error(`The field "${key}" is mandatory.`);
        return;
      }
    }

    // FAQ validation: title & value must be filled together
    for (let i = 0; i < postData.specifications.length; i++) {
      const { title, value } = postData.specifications[i];

      // If one is filled, the other becomes mandatory
      if ((title && !value) || (!title && value)) {
        toast.error(`Please fill both title and value for FAQ`);
        return;
      }
    }


    // Remove empty FAQs
    const cleanedFaqs = postData.specifications.filter(
      (faq) => faq.title && faq.value
    );

    const imagesPayload = postData.images.map((item, index) => ({
      image: item.image ? item.image.name || item.image : "dummy",
      alt_image: item.alt_image
    }));

    const formData = new FormData();
    Object.entries(postData).forEach(([key, value]) => {
      if (key !== "images" && key !== "specifications") {
        formData.append(key, value);
      }
      if (key === "specifications") {
        // ✅ ALWAYS send faqs (even empty array)
        formData.append("specifications", JSON.stringify(cleanedFaqs));
      }
      if (key === "images") {
        formData.append(key, JSON.stringify(imagesPayload))
      }
    });

    postData.images.forEach((item, index) => {
      if (item.image) {
        if (item.image instanceof File) {
          formData.append(`images[${index}][image]`, item.image);
        }
      }
    });

    try {
      const response = await ApiRequest("POST", updateEndpoint + product_id, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.error === false) {
        toast.success(response.message);
        navigate("/product");
      } else {
        toast.error(response.message)
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  // =================Add More Functionality Start=====================

  const addImageBlock = () => {
    setPostData(prev => ({
      ...prev,
      images: [...prev.images, { image: null, alt_image: "" }]
    }));

    setPreviews(prev => ({
      ...prev,
      images: [...prev.images, null]
    }));
  };

  const removeImageBlock = (index) => {
    if (index === 0) return; // first one protected

    setPostData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));

    setPreviews(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // =================Add More Functionality End=====================


  // =========FAQS Functionality

  // 🔹 handle change for title/value
  const handleFaqChange = (index, field, value) => {
    setPostData((prev) => {
      const updatedFaqs = [...prev.specifications];
      updatedFaqs[index][field] = value;
      return { ...prev, specifications: updatedFaqs };
    });
  };

  // 🔹 add new FAQ
  const addFaq = () => {
    setPostData((prev) => ({
      ...prev,
      specifications: [...prev.specifications, { title: "", value: "" }],
    }));
  };

  // 🔹 remove FAQ
  const removeFaq = (index) => {
    setPostData((prev) => ({
      ...prev,
      specifications: prev.specifications.filter((_, i) => i !== index),
    }));
  };


  const pagename = {
    name: 'Product Management',
    user: 'Admin',
    innerpage: 'Product Management / Product / Edit Product'
  }

  if (!postData) { return null; }

  return (
    <>
      <AdminHeader pagename={pagename} />
      <div className="add-content-page">
        <form id="productEdit" onSubmit={handleSubmit}>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>title<small>*</small></label>
                  </div>
                  <input value={postData?.title} onChange={handleChange} name="title" type="text" className="form-control" placeholder="Enter Title" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>slug<small>*</small></label>
                  </div>
                  <input value={postData?.slug} onChange={handleChange} name="slug" type="text" className="form-control" placeholder="Enter Slug" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>brand<small>*</small></label>
                  </div>
                  <input value={postData?.brand} onChange={handleChange} name="brand" type="text" className="form-control" placeholder="Enter Brand" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>type</label>
                  </div>
                  <input value={postData?.type} onChange={handleChange} name="type" type="text" className="form-control" placeholder="Enter Type" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>capacity</label>
                  </div>
                  <input value={postData?.capacity} onChange={handleChange} name="capacity" type="text" className="form-control" placeholder="Enter Capacity" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>No. of Rolls & Roll Dia</label>
                  </div>
                  <input value={postData?.no_of_rolls_and_roll_dia} onChange={handleChange} name="no_of_rolls_and_roll_dia" type="text" className="form-control" placeholder="Enter No. of Rolls & Roll Dia" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>model number</label>
                  </div>
                  <input value={postData?.model_number} onChange={handleChange} name="model_number" type="text" className="form-control" placeholder="Enter Model Number" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Product Category<small>*</small></label>
                  </div>
                  <CustomSelect defaultValue={postData?.category_id} onChange={handleChange} name="category_id" options={productCategory} placeholder="Select Product Category" />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Brief<small>*</small></label>
                  </div>
                  <input value={postData?.brief} onChange={handleChange} name="brief" type="text" className="form-control" placeholder="Enter continent" />
                </div>
              </div>

              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description} onChange={(event, editor) => handleEditorChange("description", editor?.getData())} />
                </div>
              </div>
            </div>
          </div>


          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-12">
                <h5>Specifications</h5>
                <div className="row">
                  {postData.specifications?.length > 0 &&
                    postData.specifications?.map((faq, index) => (
                      <div key={index} className="col-lg-4" style={{ position: "relative" }}>
                        <div className="add-page-inputs">
                          <div className="label-wrap-add">
                            <label>Name {`${index + 1}`}</label>
                          </div>
                          <input className="form-control" type="text" placeholder="Name" value={faq.title} onChange={(e) => handleFaqChange(index, "title", e.target.value)} />
                        </div>

                        <div className="add-page-inputs">
                          <div className="label-wrap-add">
                            <label>Value {`${index + 1}`}</label>
                          </div>
                          <input className="form-control" placeholder="Value" value={faq.value} onChange={(e) => handleFaqChange(index, "value", e.target.value)} />
                        </div>

                        <button onClick={() => removeFaq(index)} type="button" className="delete-faqfield">X</button>
                      </div>
                    ))
                  }

                  <div className="col-lg-12 add-page-inputs">
                    <button type="button" className="addmore-images w-100" onClick={addFaq}>Add Specifications +</button>
                  </div>

                </div>
              </div>
            </div>
          </div>


          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Thumbnail Image (480 × 546)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="thumbnail"
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    requiredWidth={480}
                    requiredHeight={546}
                    imagePreview={previews.thumbnail} />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Thumbnail Image Alt Tag<small>*</small></label>
                  </div>
                  <input onChange={handleChange} value={postData?.alt_thumbnail} name="alt_thumbnail" type="text" className="form-control" placeholder="alt tag" />
                </div>
              </div>

              {/*<div className="col-lg-4">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Banner Image (1608 × 407)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="banner_image"
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    requiredWidth={1608}
                    requiredHeight={407}
                    imagePreview={previews.banner_image} />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Banner Image Alt Tag<small>*</small></label>
                  </div>
                  <input onChange={handleChange} value={postData?.alt_banner_image} name="alt_banner_image" type="text" className="form-control" placeholder="alt tag" />
                </div>
              </div>
               */}

              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Brochure<small>*</small></label>
                  </div>
                  <CustomPdfUpload name="pdf_brochure" onPdfChange={handleChange} pdfPreview={previews?.pdf_brochure} />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <h5>Product Image</h5>

              {postData.images?.map((item, index) => (
                <div className="col-lg-6" key={index}>
                  <div className="add-page-inputs">

                    <div className="label-wrap-add">
                      <label>Product Image {index + 1} (588 × 588)<small>*</small></label>
                    </div>

                    <CustomImageUpload name={`image_${index}`} acceptedFormats={[".webp"]} imagePreview={previews.images[index]} onImageChange={(e) => handleImageChange(index, e.target.files[0])} requiredWidth={588} requiredHeight={588} />

                    <div className="label-wrap-add">
                      <label>Alt Tag Image {index + 1}<small>*</small></label>
                    </div>

                    <input type="text" className="form-control mt-2" placeholder="Alt Tag" value={item.alt_image} onChange={(e) => handleAltChange(index, e.target.value)} />

                    {index > 1 && (
                      <button type="button" className="btn btn-sm btn-danger mt-2" onClick={() => removeImageBlock(index)}>
                        Remove
                      </button>
                    )}

                  </div>
                </div>
              ))}

              <div className="col-lg-12 add-page-inputs">
                <button type="button" className="addmore-images w-100" onClick={addImageBlock}>
                  Add Image +
                </button>
              </div>
            </div>
          </div>

        </form>
        <button type="submit" form="productEdit" className="default-btn">publish<span></span></button>
      </div>
    </>
  );
}
